/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Agent alerting", description = "Set up automated notifications for agent run failures and recommendation volume drops", lastUpdatedDate = new Date(1788220800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Agent alerting",
        description: "Set up automated notifications for agent run failures and recommendation volume drops",
        lastUpdatedDate: "Tue Sep 01 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Alert triggers",
          "depth": 0,
          "id": "alert-triggers"
        }, {
          "value": "Alert severity",
          "depth": 0,
          "id": "alert-severity"
        }, {
          "value": "Notification channels",
          "depth": 0,
          "id": "notification-channels"
        }, {
          "value": "Set up alerting",
          "depth": 0,
          "id": "set-up-alerting"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      img: "img",
      ul: "ul",
      li: "li",
      ol: "ol"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers, data teams"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsx("ul", {
                children: _jsxs("li", {
                  children: ["A running ", _jsx(_components.a, {
                    href: "/ai-decisioning/agents",
                    children: "agent"
                  })]
                })
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Agent alerting notifies you about engine run failures, long-running or stalled agents, and unexpected drops in recommendation volume before they impact your campaigns."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["AI Decisioning agents support automated alerting for engine runs — the daily runs that generate and schedule AID messages for your users. The alerting system for AID agents follows the same model used for ", _jsx(_components.a, {
          href: "/syncs/alerting",
          children: "syncs"
        }), " and journeys in Hightouch."]
      }), "\n", _jsx(_components.p, {
        children: "Every agent comes pre-configured with a default run failure monitor. No setup is required for monitoring to begin."
      }), "\n", _jsxs(_components.p, {
        children: ["To access alerting, open an agent and go to the ", _jsx(_components.strong, {
          children: "Alerting"
        }), " tab."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/alerting/aid-alerting-tab.png",
          alt: "Alerting tab showing recipients and trigger configuration"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "alert-triggers",
        children: "Alert triggers"
      }), "\n", _jsx(_components.p, {
        children: "There are four alert triggers available for AID agents:"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Agent run failure:"
        }), " Triggers when one or more consecutive engine runs fail. Each engine run is responsible for generating and scheduling the AID messages sent to your users, so a failure means messages may not be produced or delivered for that cycle. The alert fires regardless of which pipeline stage caused the failure and links directly to the specific failed run, taking you to the ", _jsx(_components.strong, {
          children: "View details"
        }), " page on the ", _jsx(_components.a, {
          href: "/ai-decisioning/agent-health#view-details",
          children: "Health tab"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["From View details, you can see each pipeline stage and its status, which stage the run failed at, and an error message explaining what went wrong. For more information, see ", _jsx(_components.a, {
          href: "/ai-decisioning/agent-health#view-details",
          children: "Agent health > View details"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/alerting/alert-agent-run-failure.png",
          alt: "Agent run failure trigger configuration"
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Agent run duration:"
        }), " Triggers when an agent has been running for longer than a threshold you define (in minutes). Use this to detect runs that are taking unusually long, which may indicate a warehouse performance issue or a bottleneck in the pipeline."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/alerting/alert-agent-run-duration.png",
          alt: "Agent run duration trigger configuration"
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Agent idle time:"
        }), " Triggers when no new agent run has started within a specified number of minutes. Use this to detect situations where your agent has stopped running entirely, which could mean a scheduling issue or an upstream dependency failure."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/alerting/alert-agent-idle-time.png",
          alt: "Agent idle time trigger configuration"
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Low number of recommendations generated:"
        }), " Triggers when the number of interactions (messages) generated in an engine run falls below a threshold you define. Use this to catch unexpected drops in decision volume that may not surface as an outright failure but could signal an upstream data issue or audience eligibility problem."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/alerting/alert-low-number.png",
          alt: "Low number of recommendations trigger with custom severity thresholds"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "alert-severity",
        children: "Alert severity"
      }), "\n", _jsx(_components.p, {
        children: "Each trigger can be configured with a severity level:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Critical:"
          }), " For high-urgency issues that require immediate attention, such as a run failure."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Warning:"
          }), " For lower-urgency signals, such as a drop in recommendation volume that may warrant investigation but is not blocking."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Custom:"
          }), " Set separate thresholds for Critical and Warning severity on the same trigger. For example, fire a Warning if fewer than 500 interactions are generated and a Critical if fewer than 100."]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "notification-channels",
        children: "Notification channels"
      }), "\n", _jsx(_components.p, {
        children: "Alerts can be routed to the following destination types: Slack, Email, PagerDuty, SMS, and Webhook."
      }), "\n", _jsx(_components.p, {
        children: "Each recipient can be scoped to receive only Critical alerts or all alerts."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "set-up-alerting",
        children: "Set up alerting"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to any AID agent and select the ", _jsx(_components.strong, {
            children: "Alerting"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Review the pre-configured ", _jsx(_components.strong, {
            children: "Agent run failure"
          }), " monitor. Adjust the severity or switch to a Custom threshold if needed."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click the ", _jsx(_components.strong, {
            children: "+"
          }), " button under ", _jsx(_components.strong, {
            children: "Recipients"
          }), " to add a notification channel (Slack, Email, PagerDuty, SMS, or Webhook)."]
        }), "\n", _jsxs(_components.li, {
          children: ["Optionally, configure additional triggers such as ", _jsx(_components.strong, {
            children: "Agent run duration"
          }), ", ", _jsx(_components.strong, {
            children: "Agent idle time"
          }), ", or ", _jsx(_components.strong, {
            children: "Low number of recommendations generated"
          }), " with your preferred thresholds and severity."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Save changes"
          }), ". Alerts fire on the next qualifying run."]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/ai-decisioning/agent-health",
            children: "Agent health"
          }), " to monitor pipeline runs and delivery rates."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/ai-decisioning/insights",
            children: "Insights"
          }), " to measure agent and message performance."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/ai-decisioning/agents",
            children: "Agents"
          }), " to configure goals, audiences, and scheduling."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
